/****************************************************
 * Module Name:  viewPacketPlan.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import logo from '../../../images/pansuriya.jpg'
import { Box, Button, CircularProgress, Stack, TextField, Tooltip, Typography } from "@mui/material";
import Constants from "../../../utility/constants";
import { BiSearchAlt2 } from "react-icons/bi";
import { LoadingButton } from "@mui/lab";
import Utility from "../../../utility/utility";
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";
import CommonSnackbar from "../../common/commonSnackBar";
import renderCellExpand from "../../common/dataGridCellExpand";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import KapanHistoryModal from "./kapanHistoryModal";
import PacketHistoryModal from "../../common/packetHistoryModal";

const ViewPacketPlan = ({ kapanid, packetid, handleClose }) => {

    const [data, setData] = useState([]);
    const [planLoader, setPlanLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = () => {
        setPlanLoader(true);
        let data = {
            userid: Constants.loggedInUser.id,
            kapanid: parseInt(kapanid),
            packetid: packetid,
            status: 'LOCKED',
            date: null,
            ischecker: true,
        };
        PricePlanningProxy.getAllPricePlanningByFilters(data, (responseData) => {
            if (responseData) {
                const transformedData = responseData.map(item => ({
                    ...item,
                    priceplanningdetail: item.priceplanningdetail.map(detail => ({
                        ...detail,
                        stonetype: detail.stonetype === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
                    })),
                }));

                // Flatten and process the details for filter values
                const response = transformedData.flatMap(item => item.priceplanningdetail);
                const uniqueFilterValues = {
                    createdbyusername: Utility.getDistinctOptions(response, "createdbyusername"),
                    shape: Utility.getDistinctOptions(response, "shape"),
                    color: Utility.getDistinctOptions(response, "color"),
                    cut: Utility.getDistinctOptions(response, "cut"),
                    pol: Utility.getDistinctOptions(response, "pol"),
                    sym: Utility.getDistinctOptions(response, "sym"),
                    clarity: Utility.getDistinctOptions(response, "clarity"),
                    flo: Utility.getDistinctOptions(response, "flo"),
                    stonetype: Utility.getDistinctOptions(response, "stonetype"),
                    lockedbyusername: Utility.getDistinctOptions(response, "lockedbyusername"),
                    status: Utility.getDistinctOptions(response, "status"),
                    lab: Utility.getDistinctOptions(response, "lab"),
                };

                setFilterValues(uniqueFilterValues);
                setPlanLoader(false);
                setData(transformedData);
            } else {
                setPlanLoader(false);
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
            }
        });
    }

    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }

    // Outer Columns
    const initialColumns = () => [
        {
            field: 'kapanid',
            headerName: 'KAPANID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: (params) => Utility.NeedToShowKapanDetailModal() ? (
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: "black"
                    }}
                    onClick={() => setShowKapanDetailModal(true)}
                >
                    {params.value}
                </Button>) :
                <div>{params.value}</div>,
        },
        {
            field: 'packetid',
            headerName: 'PACKETID',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            valueFormatter: (params) => params.value,
            renderCell: (params) =>
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: "black"
                    }}
                    onClick={() => setShowPacketDetailModal(true)}
                >
                    {params.value}
                </Button>,
        },
        {
            field: 'planid',
            headerName: 'PLAN ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalcount',
            headerName: 'COUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalsize',
            headerName: 'TOTAL SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalstoneprice',
            headerName: 'TOTAL STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalprice',
            headerName: 'TOTAL PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },

    ];
    const columns = initialColumns();

    // Inner Columns
    const middleColumns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'kapanid',
            headerName: 'KAPANID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'packetid',
            headerName: 'PACKETID',
            headerAlign: 'center',
            align: 'center',
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'size',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'lab',
            headerName: 'LAB',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value,
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value,
            width: 150
        },
        {
            field: 'rapnetprice',
            headerName: 'RAP',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            editable: true,
            valueFormatter: (params) => params.value,
        },
        {
            field: 'labprice',
            headerName: 'LAB PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'total',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'stoneprice',
            headerName: 'STONE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 120
        },
        // {
        //     field: 'view',
        //     headerName: 'VIEW',
        //     headerAlign: 'center',
        //     align: 'center',
        //     width: 100,
        //     renderCell: (params) => (
        //         <button
        //             style={{
        //                 padding: '5px 10px',
        //                 cursor: 'pointer',
        //                 backgroundColor: '#007bff',
        //                 color: '#fff',
        //                 border: 'none',
        //                 borderRadius: '5px',
        //             }}
        //             onClick={() => handleViewButtonClick(params.row.id)}>
        //             VIEW
        //         </button>
        //     ),
        // },
        {
            field: 'iscertified',
            headerName: 'IS CERTIFIED',
            headerAlign: 'center',
            align: 'center',
            type: "boolean",
            width: 100
        },
        {
            field: 'createdby',
            headerName: 'CREATED BY ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'createdbyusername',
            headerName: 'CREATED BY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.createdbyusername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'lockedby',
            headerName: 'LOCKED BY ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'lockedbyusername',
            headerName: 'LOCKED BY',
            headerAlign: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lockedbyusername : [],
            filterOperators: Utility.combinedFilters(),
            align: 'center',
            width: 100
        },
        {
            field: 'createdat',
            headerName: 'CREATED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 150
        },
        {
            field: 'updatedat',
            headerName: 'UPDATED AT',
            headerAlign: 'center',
            align: 'center',
            width: 110,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: 'status',
            headerName: 'STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'rapnetdate',
            headerName: 'RAPNET DATE',
            headerAlign: 'center',
            align: 'center',
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
    ], [filterValues]);

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row) => <DetailPanelContent row={row} />,
        [middleColumns],);

    function DetailPanelContent({ row: rowProp }) {

        let rows = rowProp.priceplanningdetail;

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "85vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '30vh',
                        }}
                        density="compact"
                        columns={middleColumns}
                        rows={rows}
                        getRowId={(row) => row.id}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    createdat: false,
                                    kapanid: false,
                                    packetid: false,
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    return (
        <Box>
            <Modal
                backdrop="static"
                show={true}
                onHide={handleClose}
                dialogClassName="bhb"
                enforceFocus={false}
            >
                {showKapanDetailModal ? (
                    <KapanHistoryModal
                        show={showKapanDetailModal}
                        kapanId={kapanid}
                        close={() => setShowKapanDetailModal(false)}
                    />
                ) : null}

                {showPacketDetailModal ? (
                    <PacketHistoryModal
                        show={showPacketDetailModal}
                        packetId={packetid}
                        kapanId={kapanid}
                        close={() => setShowPacketDetailModal(false)}
                    />
                ) : null}

                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Error" : "Warning"
                    }
                    handleClose={() => setShowSnackbar(false)}
                />

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;View Packet Plan
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    {/* Filters */}
                    <Box sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "30px",
                        width: "50%",
                        paddingTop: "8px",
                        "& .MuiOutlinedInput-input": {
                            padding: "9px !important",
                            height: "25px !important"
                        },
                        "& .MuiInputLabel-root": {
                            top: "-7px !important"
                        },
                        "& .MuiOutlinedInput-root": {
                            padding: 0
                        }
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                            <Typography sx={{ width: "25%", mr: 2 }}>Kapan Id</Typography>
                            <TextField
                                sx={{ width: "75%", margin: 0 }}
                                margin="normal"
                                label="Kapan Id"
                                placeholder="Enter Kapan Id"
                                value={kapanid}
                                disabled
                            />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                            <Typography sx={{ width: "25%", mr: 2 }}>Packet Id</Typography>
                            <TextField
                                sx={{ width: "75%", margin: 0 }}
                                margin="normal"
                                label="Packet Id"
                                placeholder="Enter Packet Id"
                                multiline
                                value={packetid}
                                disabled
                            />
                        </Box>
                        <Box sx={{
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }

                        }}>
                            <LoadingButton
                                variant="contained"
                                color="info"
                                onClick={() => handleSearch()}
                                loading={planLoader}
                                loadingPosition="start"
                                startIcon={<BiSearchAlt2 />}
                                sx={{ height: 43 }}
                            >
                                Search
                            </LoadingButton>
                        </Box>
                    </Box>

                    {/* DataGridPro */}
                    <Box
                        sx={{
                            width: "100%",
                            backgroundColor: '#fff',
                            color: '#000',
                            position: "relative",
                            paddingTop: "20px",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "70vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            loading={planLoader}
                            slots={{ toolbar: GridToolbar }}
                            rows={data}
                            columns={columns}
                            getRowId={(row) => row.embededkey}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        id: false,
                                        createdby: false,
                                        createdbyusername: false,
                                        lockedby: false,
                                        status: false,
                                        rapnetdate: false,
                                        createdat: false
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                                sorting: {
                                    sortModel: [{ field: "updatedat", sort: "desc" }],
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                            getDetailPanelHeight={getDetailPanelHeight}
                            getDetailPanelContent={({ row }) => getDetailPanelContent(row)}
                        />

                        <Box
                            style={{
                                position: "absolute",
                                top: "27px",
                                right: "260px",
                                display: "flex",
                                gap: "15px",
                            }}
                        >
                            {planLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() =>
                                            handleSearch()
                                        }
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Box>
    );
}

export default ViewPacketPlan;